import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  apiClient,
  ApiError,
  feeAfterShirtOptOut,
  feeForDaysCents,
  shirtOptOutCents,
  type AdminChild,
  type AdminEnrolmentInput,
  type Program,
} from "@alight/contracts";
import {
  Banner,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
} from "@alight/ui";
import { Check, UserPlus, Users } from "lucide-react";

/**
 * MANUAL ENROLMENT — the host adds a child to a program without waiting on a
 * public submission. Two ways in, one dialog:
 *   - "Existing child" — pick someone already on record (a returning family
 *     whose parent hasn't re-submitted, or a message that they've paid), and
 *   - "New person" — type a walk-up's details on the day.
 * The host sets the payment outcome (unpaid → chase in Reconciliation, or paid
 * → recorded on the ledger). Server: POST /api/admin/enrolments.
 *
 * Controlled: the caller owns `open`. Pass `program` to lock the enrolment to a
 * specific program (the Attendance screen does this — the roster you're looking
 * at); omit it to let the host choose from the program list.
 */

type Mode = "existing" | "new";
type PayStatus = "unpaid" | "paid";

const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? "";

export function ManualEnrolmentDialog({
  open,
  onOpenChange,
  program,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Lock the enrolment to this program (hides the program picker). */
  program?: Program | null;
  onCreated?: () => void;
}) {
  const api = React.useMemo(() => apiClient(API_BASE), []);
  const qc = useQueryClient();

  const [mode, setMode] = React.useState<Mode>("existing");
  const [childId, setChildId] = React.useState<number | null>(null);
  const [childSearch, setChildSearch] = React.useState("");
  const [parentName, setParentName] = React.useState("");
  const [parentEmail, setParentEmail] = React.useState("");
  const [parentPhone, setParentPhone] = React.useState("");
  const [childName, setChildName] = React.useState("");
  const [yearLevel, setYearLevel] = React.useState("");
  const [programId, setProgramId] = React.useState<string>(program ? String(program.id) : "");
  const [payStatus, setPayStatus] = React.useState<PayStatus>("unpaid");
  const [payMethod, setPayMethod] = React.useState<"cash" | "bank_transfer" | "other">("cash");
  /* Default ON for a debt, OFF for a payment already taken: an unpaid walk-up
     is the whole reason this exists, while a family who just handed over cash
     is standing right there and does not need a receipt chasing them home.
     Switching the payment outcome moves it, so the tick always matches the
     choice above it — the host can still override either way. */
  const [notifyParent, setNotifyParent] = React.useState(true);
  /** Set when the enrolment saved but its email did not — see the mutation. */
  const [notifyWarning, setNotifyWarning] = React.useState<string | null>(null);
  const [formError, setFormError] = React.useState<string | null>(null);
  /** PART-DAY: which days this child is coming to, on a program that runs on
   *  named days. Defaults to ALL of them — the door has to be able to say
   *  "Wednesday only", but the ordinary case is a family coming to the lot. */
  const [days, setDays] = React.useState<string[]>([]);
  /** THE SHIRT OPT-OUT: this walk-up said they don't need a program shirt, so
   *  the program's opt-out amount comes off — the same offer the public form
   *  makes, because the host quoting one price at the door and the form
   *  quoting another is the kind of thing families remember. */
  const [noShirt, setNoShirt] = React.useState(false);

  // Reset the whole form each time the dialog opens (fresh every enrolment).
  React.useEffect(() => {
    if (!open) return;
    setMode("existing");
    setChildId(null);
    setChildSearch("");
    setParentName("");
    setParentEmail("");
    setParentPhone("");
    setChildName("");
    setYearLevel("");
    setProgramId(program ? String(program.id) : "");
    setPayStatus("unpaid");
    setPayMethod("cash");
    setNotifyParent(true);
    setNotifyWarning(null);
    setFormError(null);
    // Re-tick every day: the program hasn't changed between opens, so the
    // effect below won't fire, and last enrolment's "Wednesday only" must not
    // silently become this one's.
    setDays(program?.days ?? []);
    // Same reason as the days: last enrolment's "no shirt" must not quietly
    // discount this one.
    setNoShirt(false);
  }, [open, program]);

  const childrenQuery = useQuery({
    queryKey: ["admin", "children"],
    queryFn: () => api.adminListChildren(),
    enabled: open && mode === "existing",
    staleTime: 30 * 1000,
  });

  const programsQuery = useQuery({
    queryKey: ["admin", "programs"],
    queryFn: () => api.adminListPrograms(),
    enabled: open && !program,
    staleTime: 5 * 60 * 1000,
  });
  // Only currently-running programs can be enrolled into — a finished term
  // (active = 0, e.g. Term 2) must never be offered for a NEW enrolment, or the
  // host ends up adding children to a term that's already over. Attendance
  // backfill into a past term goes through the locked-`program` path instead.
  const programs = React.useMemo(
    () =>
      (programsQuery.data ?? [])
        .filter((p) => p.active === 1)
        .sort((a, b) => a.sort_order - b.sort_order || a.id - b.id),
    [programsQuery.data],
  );

  // The program this enrolment is going into — the locked one, or whichever the
  // host picked. Its `days` decide whether the day picker appears at all.
  const targetProgram = React.useMemo(
    () => program ?? programs.find((p) => String(p.id) === programId) ?? null,
    [program, programs, programId],
  );
  const programDays = React.useMemo(() => targetProgram?.days ?? [], [targetProgram]);
  /** What this program takes off for going without a shirt. 0 = it doesn't
   *  offer the opt-out, and no tick is shown. */
  const shirtOptOutDiscount = targetProgram ? shirtOptOutCents(targetProgram) : 0;

  // Every day ticked by default, and re-ticked whenever the program changes, so
  // switching programs can never leave a stale set of days behind.
  React.useEffect(() => {
    setDays(programDays);
  }, [programDays]);

  const selectedChild = React.useMemo(
    () => (childrenQuery.data ?? []).find((ch) => ch.id === childId) ?? null,
    [childrenQuery.data, childId],
  );

  // How many name rows to render at once. The list lives inside the dialog's
  // single scroll area, so this is only a perf guard — narrow with the search
  // box rather than scrolling a huge list.
  const CHILD_LIMIT = 40;
  const childMatches = React.useMemo(() => {
    const all = childrenQuery.data ?? [];
    const q = childSearch.trim().toLowerCase();
    const matched = q
      ? all.filter((ch) =>
          [ch.name, ch.parent_name ?? "", ch.parent_email].join(" ").toLowerCase().includes(q),
        )
      : all;
    return { total: matched.length, shown: matched.slice(0, CHILD_LIMIT) };
  }, [childrenQuery.data, childSearch]);
  const filteredChildren = childMatches.shown;

  function pickChild(ch: AdminChild) {
    setChildId(ch.id);
    setParentName(ch.parent_name ?? "");
    setParentEmail(ch.parent_email);
    setParentPhone(ch.parent_phone ?? "");
    setChildName(ch.name);
    // Default to the record's current year level, but leave it editable — a
    // returning child may have moved up a year for the new term.
    setYearLevel(ch.year_level ?? "");
    setFormError(null);
  }

  const create = useMutation({
    mutationFn: (body: AdminEnrolmentInput) => api.adminCreateEnrolment(body),
    onSuccess: (created) => {
      // Everything that reads enrolments/rosters: submissions list, children,
      // attendance (roster + report), reconciliation. Invoices too, now that a
      // manual enrolment raises one.
      qc.invalidateQueries({ queryKey: ["admin", "enrolments"] });
      qc.invalidateQueries({ queryKey: ["admin", "children"] });
      qc.invalidateQueries({ queryKey: ["admin", "att"] });
      qc.invalidateQueries({ queryKey: ["reconcile"] });
      qc.invalidateQueries({ queryKey: ["admin", "invoices"] });
      onCreated?.();

      /* The enrolment is saved either way — sending is best-effort. If the
         host asked for an email and it did not go, HOLD the dialog open and
         say so: closing on a green tick would leave them believing a family
         had been chased when nothing left the building. */
      if (notifyParent && !created.notified) {
        setNotifyWarning(created.notify_error ?? "the email could not be sent");
        return;
      }
      onOpenChange(false);
    },
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);

    if (!programId) {
      setFormError("Choose a program to enrol into.");
      return;
    }
    if (mode === "existing" && childId === null) {
      setFormError("Pick a child from the list.");
      return;
    }
    if (!parentName.trim() || !childName.trim()) {
      setFormError("Parent/guardian name and child name are both required.");
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(parentEmail.trim())) {
      setFormError("Enter a valid parent/guardian email.");
      return;
    }
    if (!yearLevel.trim()) {
      setFormError("Enter the child's year level.");
      return;
    }
    if (programDays.length > 0 && days.length === 0) {
      setFormError("Tick at least one day this child is coming to.");
      return;
    }

    create.mutate({
      program_id: programId,
      child_id: mode === "existing" && childId !== null ? childId : undefined,
      parent_name: parentName.trim(),
      parent_email: parentEmail.trim(),
      parent_phone: parentPhone.trim() || undefined,
      child_name: childName.trim(),
      year_level: yearLevel.trim(),
      payment_status: payStatus,
      payment_method: payMethod,
      notify_parent: notifyParent,
      days: programDays.length > 0 ? days : undefined,
      // The server ignores it on a program that doesn't offer the opt-out, so
      // sending it unconditionally can never invent a discount.
      no_shirt: noShirt || undefined,
    });
  }

  const detailsFilled = mode === "new" || childId !== null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>Add an enrolment</DialogTitle>
          <DialogDescription>
            Enrol a child into {program ? <strong>{program.title}</strong> : "a program"} yourself
            — a walk-up on the day, or a returning family who hasn&apos;t re-submitted this term.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
          {/* Scrollable body — the dialog itself never grows past 85vh; this
              region takes the overflow so the header + footer stay in view. */}
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-0.5 py-1">
          {/* Existing vs new */}
          <div className="grid grid-cols-2 gap-2">
            <ModeButton
              active={mode === "existing"}
              onClick={() => {
                setMode("existing");
                setFormError(null);
              }}
              icon={<Users className="h-4 w-4" aria-hidden />}
              label="Existing child"
              hint="Already on record"
            />
            <ModeButton
              active={mode === "new"}
              onClick={() => {
                setMode("new");
                setChildId(null);
                setParentName("");
                setParentEmail("");
                setParentPhone("");
                setChildName("");
                setYearLevel("");
                setFormError(null);
              }}
              icon={<UserPlus className="h-4 w-4" aria-hidden />}
              label="New person"
              hint="Type their details"
            />
          </div>

          {/* Existing child picker */}
          {mode === "existing" ? (
            selectedChild ? (
              <div className="flex items-start justify-between gap-3 rounded-card border border-border bg-paper px-3 py-2">
                <div>
                  <div className="font-medium text-ink">{selectedChild.name}</div>
                  <div className="text-xs text-muted">
                    {selectedChild.parent_name ?? "—"} · {selectedChild.parent_email}
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setChildId(null);
                    setChildSearch("");
                  }}
                >
                  Change
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <Input
                  type="search"
                  value={childSearch}
                  onChange={(e) => setChildSearch(e.target.value)}
                  placeholder="Search child or parent name…"
                  aria-label="Search children"
                />
                <div className="divide-y divide-border rounded-card border border-border">
                  {childrenQuery.isLoading ? (
                    <p className="px-3 py-4 text-center text-sm text-muted">Loading children…</p>
                  ) : filteredChildren.length === 0 ? (
                    <p className="px-3 py-4 text-center text-sm text-muted">
                      No children match. Switch to <strong>New person</strong> to add someone not on
                      record yet.
                    </p>
                  ) : (
                    filteredChildren.map((ch) => (
                      <button
                        key={ch.id}
                        type="button"
                        onClick={() => pickChild(ch)}
                        className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left transition-colors hover:bg-paper"
                      >
                        <span>
                          <span className="block font-medium text-ink">{ch.name}</span>
                          <span className="block text-xs text-muted">
                            {ch.year_level ? `${ch.year_level} · ` : ""}
                            {ch.parent_name ?? ch.parent_email}
                          </span>
                        </span>
                      </button>
                    ))
                  )}
                </div>
                {childMatches.total > filteredChildren.length ? (
                  <p className="text-xs text-muted">
                    Showing the first {filteredChildren.length} of {childMatches.total}. Type a name
                    to narrow the list.
                  </p>
                ) : null}
              </div>
            )
          ) : null}

          {/* Details — filled in for a new person, shown read-onlyish for an
              existing pick (name/email come from the record; year is editable). */}
          {detailsFilled ? (
            <div className="space-y-3">
              {mode === "new" ? (
                <>
                  <Field
                    id="me-parent-name"
                    label="Parent / guardian name"
                    value={parentName}
                    onChange={setParentName}
                    placeholder="Jane Smith"
                  />
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Field
                      id="me-parent-email"
                      label="Parent email"
                      type="email"
                      value={parentEmail}
                      onChange={setParentEmail}
                      placeholder="jane@example.com"
                    />
                    <Field
                      id="me-parent-phone"
                      label="Parent phone (optional)"
                      value={parentPhone}
                      onChange={setParentPhone}
                      placeholder="0400 000 000"
                    />
                  </div>
                  <Field
                    id="me-child-name"
                    label="Child name"
                    value={childName}
                    onChange={setChildName}
                    placeholder="Alex Smith"
                  />
                </>
              ) : null}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field
                  id="me-year"
                  label="Year level"
                  value={yearLevel}
                  onChange={setYearLevel}
                  placeholder="Year 4"
                />
                {!program ? (
                  <div className="space-y-1.5">
                    <Label htmlFor="me-program">Program</Label>
                    <Select value={programId} onValueChange={setProgramId}>
                      <SelectTrigger id="me-program">
                        <SelectValue placeholder="Choose a program" />
                      </SelectTrigger>
                      <SelectContent>
                        {programs.map((p) => (
                          <SelectItem key={p.id} value={String(p.id)}>
                            {p.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : null}
              </div>

              {/* PART-DAY: which days they are coming to. Only on a program
                  that runs on named days; the fee follows the ticks, exactly
                  as it does on the public form. */}
              {programDays.length > 0 ? (
                <div className="space-y-1.5">
                  <Label>Days they are coming</Label>
                  <div className="space-y-2 rounded-card border border-border bg-paper p-3">
                    {programDays.map((d) => {
                      const on = days.includes(d);
                      return (
                        <label
                          key={d}
                          className="flex cursor-pointer items-center gap-3 text-sm text-ink"
                        >
                          <Checkbox
                            checked={on}
                            onCheckedChange={(v) =>
                              setDays((prev) =>
                                v === true
                                  ? // Kept in the program's order, so the roll
                                    // never depends on the ticking order.
                                    programDays.filter((x) => x === d || prev.includes(x))
                                  : prev.filter((x) => x !== d),
                              )
                            }
                          />
                          <span className="font-medium">{d}</span>
                        </label>
                      );
                    })}
                    <p className="text-xs text-muted">
                      {days.length === 0
                        ? "Tick at least one day."
                        : `${days.length} of ${programDays.length} days${
                            noShirt ? ", no shirt" : ""
                          } · $${(
                            feeAfterShirtOptOut(
                              feeForDaysCents(
                                targetProgram?.day_pricing,
                                days.length,
                                targetProgram?.amount_cents ?? 0,
                              ),
                              shirtOptOutDiscount,
                              noShirt,
                            ) / 100
                          ).toFixed(2)} — they only appear on the roll for the days ticked.`}
                    </p>
                  </div>
                </div>
              ) : null}

              {/* THE SHIRT OPT-OUT — the same offer the public form makes, so
                  a walk-up who says "she's got one already" can be entered at
                  the price they were quoted. */}
              {shirtOptOutDiscount > 0 ? (
                <label className="flex cursor-pointer items-start gap-3 rounded-card border border-border bg-paper p-3 text-sm text-ink">
                  <Checkbox
                    checked={noShirt}
                    onCheckedChange={(v) => setNoShirt(v === true)}
                    className="mt-0.5"
                  />
                  <span>
                    <span className="font-medium">They don’t need a program shirt</span>
                    <span className="mt-0.5 block text-xs text-muted">
                      Takes ${(shirtOptOutDiscount / 100).toFixed(2)} off the fee and records
                      that no shirt is to be ordered.
                    </span>
                  </span>
                </label>
              ) : null}

              {/* Payment outcome */}
              <div className="space-y-1.5">
                <Label>Payment</Label>
                <div className="grid grid-cols-2 gap-2">
                  <PayButton
                    active={payStatus === "unpaid"}
                    onClick={() => {
                      setPayStatus("unpaid");
                      setNotifyParent(true);
                    }}
                    label="Not paid yet"
                    hint="Chase in Reconciliation"
                  />
                  <PayButton
                    active={payStatus === "paid"}
                    onClick={() => {
                      setPayStatus("paid");
                      setNotifyParent(false);
                    }}
                    label="Already paid"
                    hint="Record it now"
                  />
                </div>
              </div>

              {payStatus === "paid" ? (
                <div className="space-y-1.5">
                  <Label htmlFor="me-method">How they paid</Label>
                  <Select value={payMethod} onValueChange={(v) => setPayMethod(v as typeof payMethod)}>
                    <SelectTrigger id="me-method">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ) : null}

              {/* Tell the parent. An invoice is raised either way (it lands on
                  the Invoices screen and can be re-sent from there); this is
                  only about whether we email it now. Last, because it acts on
                  the payment outcome chosen above it. */}
              <label
                htmlFor="me-notify"
                className="flex cursor-pointer items-start gap-3 rounded-card border border-border bg-paper p-3"
              >
                <Checkbox
                  id="me-notify"
                  checked={notifyParent}
                  onCheckedChange={(v) => setNotifyParent(v === true)}
                  className="mt-0.5"
                />
                <span>
                  <span className="block text-sm font-medium text-ink">
                    {payStatus === "paid"
                      ? "Email the parent a receipt"
                      : "Email the parent their invoice"}
                  </span>
                  <span className="block text-xs text-muted">
                    {payStatus === "paid"
                      ? "Confirms what they paid, with the reference."
                      : "Amount, reference, bank details and a Pay now link — it works without them signing in."}
                  </span>
                </span>
              </label>
            </div>
          ) : null}

          {notifyWarning ? (
            <Banner variant="warning" title={`${childName || "The child"} is enrolled — but not emailed`}>
              We saved the enrolment, but {notifyWarning}. The invoice is on the{" "}
              <strong>Invoices</strong> screen, so you can send it from there when you&apos;re
              ready.
            </Banner>
          ) : null}

          {formError ? (
            <p className="text-sm text-accent-fg" role="alert">
              {formError}
            </p>
          ) : null}
          {create.error ? (
            <p className="text-sm text-accent-fg" role="alert">
              {create.error instanceof ApiError
                ? errorMessage(create.error)
                : "Couldn’t add the enrolment — please retry."}
            </p>
          ) : null}

          </div>

          <DialogFooter className="mt-4 shrink-0 border-t border-border pt-4">
            {/* Once the enrolment is saved there is nothing left to submit —
                re-sending would only collide with the duplicate guard. */}
            {notifyWarning ? (
              <Button type="button" onClick={() => onOpenChange(false)}>
                Done
              </Button>
            ) : (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={create.isPending}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={create.isPending || !detailsFilled}>
                  {create.isPending ? "Adding…" : "Add enrolment"}
                </Button>
              </>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Pull the human message out of an ApiError body (the worker sends { error }). */
function errorMessage(err: ApiError): string {
  const body = err.body as { error?: string } | undefined;
  return body?.error ?? "Couldn’t add the enrolment — please retry.";
}

function ModeButton({
  active,
  onClick,
  icon,
  label,
  hint,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  hint: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex flex-col items-start gap-0.5 rounded-card border px-3 py-2 text-left transition-colors",
        active
          ? "border-primary bg-primary/10 text-ink"
          : "border-border text-muted hover:bg-paper",
      )}
    >
      <span className="flex items-center gap-1.5 text-sm font-semibold text-ink">
        {icon}
        {label}
      </span>
      <span className="text-xs text-muted">{hint}</span>
    </button>
  );
}

function PayButton({
  active,
  onClick,
  label,
  hint,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  hint: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "relative flex flex-col items-start gap-0.5 rounded-card border px-3 py-2 text-left transition-colors",
        active ? "border-primary bg-primary/10" : "border-border hover:bg-paper",
      )}
    >
      {active ? <Check className="absolute right-2 top-2 h-4 w-4 text-primary" aria-hidden /> : null}
      <span className="text-sm font-semibold text-ink">{label}</span>
      <span className="text-xs text-muted">{hint}</span>
    </button>
  );
}

function Field({
  id,
  label,
  value,
  onChange,
  placeholder,
  type = "text",
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </div>
  );
}
